"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { useAuth } from "@yfp/context/AuthContext";
import { useRouter } from "next/navigation";
import { Button } from "@yfp/components/ui/button";
import { ROUTES } from "@yfp/constants/routes";
import { trackLogin } from "@yfp/utils/analytics";

const BACKGROUND_IMAGES = [
  {
    src: "https://images.unsplash.com/photo-1635111031688-9b13c0125d12?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080",
    className:
      "absolute left-8 top-8 h-32 w-48 rotate-[-8deg] rounded-2xl opacity-20",
  },
  {
    src: "https://images.unsplash.com/photo-1763479142280-675629f6db27?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080",
    className:
      "absolute right-12 top-16 h-36 w-56 rotate-[6deg] rounded-2xl opacity-15",
  },
  {
    src: "https://images.unsplash.com/photo-1758424106282-6d8476c33b19?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080",
    className:
      "absolute left-4 top-1/3 h-44 w-44 rotate-[-12deg] rounded-2xl opacity-[0.12]",
  },
  {
    src: "https://images.unsplash.com/photo-1759300633209-ecb9cfbc1cb2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080",
    className:
      "absolute right-8 top-1/2 h-40 w-52 rotate-[10deg] rounded-2xl opacity-[0.18]",
  },
  {
    src: "https://images.unsplash.com/photo-1760094129328-dc1c0ae6eb26?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080",
    className:
      "absolute bottom-20 left-16 h-44 w-60 rotate-[5deg] rounded-2xl opacity-[0.16]",
  },
  {
    src: "https://images.unsplash.com/photo-1560990883-9b76fec399a9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080",
    className:
      "absolute bottom-12 right-20 h-36 w-48 rotate-[-7deg] rounded-2xl opacity-[0.14]",
  },
];

const LOGIN_FIELDS = [
  {
    id: "email",
    type: "email",
    label: "Email",
    placeholder: "you@company.com",
  },
  {
    id: "password",
    type: "password",
    label: "Password",
    placeholder: "••••••••",
  },
] as const;

export default function LoginPage() {
  const { login, contractor, loading } = useAuth();
  const router = useRouter();
  const [formValues, setFormValues] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (contractor) {
      router.push(ROUTES.dashboard);
    }
  }, [contractor, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await login(formValues.email, formValues.password);
      trackLogin();
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string };
      if (
        firebaseError.code === "auth/user-not-found" ||
        firebaseError.code === "auth/wrong-password" ||
        firebaseError.code === "auth/invalid-credential"
      ) {
        setError("Invalid email or password");
      } else {
        setError(firebaseError.message || "Login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0a0a0a]">
        <div className="text-white/50">Loading...</div>
      </div>
    );
  }

  return (
    <div className="relative flex h-screen overflow-hidden bg-[#0a0a0a]">
      {/* Background collage */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {BACKGROUND_IMAGES.map((img, i) => (
          <div
            key={i}
            className={`${img.className} overflow-hidden shadow-2xl`}
          >
            <Image
              src={img.src}
              alt=""
              fill
              sizes="240px"
              className="object-cover"
            />
          </div>
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-1 items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-[400px]">
          <div className="mb-6 text-center md:mb-8">
            <h1 className="font-arsenal mb-4 text-[34px] leading-tight tracking-[-0.6px] text-white md:text-[52px] md:tracking-[-1.2px]">
              Yardzen for Pros
            </h1>
            <p className="font-dm-sans text-[15px] leading-relaxed text-white/60 md:text-[17px]">
              Capture property info on-site with clients and generate AI-powered
              landscape design concepts. Built for contractors.
            </p>
          </div>

          <div className="rounded-[var(--radius-xl)] border border-white/10 bg-[rgba(18,18,18,0.85)] p-5 shadow-[0_10px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl md:p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              {LOGIN_FIELDS.map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={field.id}
                    className="font-dm-sans mb-2 block text-[13px] font-medium text-white/70"
                  >
                    {field.label}
                  </label>
                  <input
                    id={field.id}
                    type={field.type}
                    value={formValues[field.id]}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        [field.id]: e.target.value,
                      }))
                    }
                    placeholder={field.placeholder}
                    className="font-dm-sans focus:border-[var(--primary)]/50 focus:ring-[var(--primary)]/35 h-[48px] w-full rounded-xl border border-[var(--input-border)] bg-[var(--input-background)] px-4 text-[15px] text-white transition-all placeholder:text-white/30 focus:outline-none focus:ring-2"
                    required
                  />
                </div>
              ))}

              {error && (
                <p className="font-dm-sans text-[13px] text-red-400">{error}</p>
              )}

              <Button
                type="submit"
                size="lg"
                className="mt-2 w-full font-semibold shadow-lg"
                disabled={submitting}
              >
                {submitting ? "Signing in..." : "Log In"}
              </Button>
            </form>

            <p className="font-dm-sans mt-5 text-center text-[14px] text-white/50">
              Don&apos;t have an account?{" "}
              <button
                onClick={() => router.push(ROUTES.signup)}
                className="text-[#626e58] underline transition-colors hover:text-[#6d7a62]"
              >
                Sign up
              </button>
            </p>
            <p className="font-dm-sans mt-2 text-center text-[14px] text-white/50">
              Forgot your password?{" "}
              <button
                onClick={() => router.push(ROUTES.forgotPassword)}
                className="text-[#626e58] underline transition-colors hover:text-[#6d7a62]"
              >
                Reset password
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
